import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../services/firebase';
import realtorApiService from '../services/realtorApiService';
import { saveDDFListingToFirestore } from '../services/ddfSyncService';

const DDFSyncTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [savedListings, setSavedListings] = useState<string[]>([]);
  const [debugData, setDebugData] = useState<any>(null);
  const [firestoreData, setFirestoreData] = useState<any>(null);

  const inspectFirestoreListings = async () => {
    try {
      setLoading(true);
      setStatus('Inspecting Firestore ddfListings...');

      const q = query(collection(db, 'ddfListings'), limit(3));
      const querySnapshot = await getDocs(q);
      
      const listings = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.basic?.title || 'No title',
          hasMediaImages: !!data.media?.images,
          imageCount: data.media?.images?.length || 0,
          images: data.media?.images || [],
          sampleImages: (data.media?.images || []).slice(0, 3)
        };
      });

      console.log('Firestore listings data:', listings);
      setFirestoreData(listings);
      setStatus(`✅ Inspected ${listings.length} listings from Firestore`);
    } catch (error: any) {
      console.error('Error inspecting Firestore:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadAndSaveSamples = async () => {
    try {
      setLoading(true);
      setStatus('Fetching business listings from DDF API...');

      // Fetch 3 business listings (feed is already filtered to businesses)
      const properties = await realtorApiService.getAllProperties({
        $top: 3
      });

      if (properties.length === 0) {
        setStatus('No business listings found in DDF API');
        return;
      }

      setStatus(`Found ${properties.length} business listings. Saving to Firestore...`);

      // Debug: Check Media data
      const mediaDebug = properties.map((p, idx) => ({
        index: idx + 1,
        listingKey: p.ListingKey,
        hasMedia: !!p.Media,
        mediaCount: p.Media?.length || 0,
        photoCount: p.PhotosCount || 0,
        mediaTypes: p.Media?.map(m => `${m.MediaType}-${m.MediaCategory}`).slice(0, 5) || [],
        sampleUrls: p.Media?.filter(m => m.MediaURL).map(m => m.MediaURL).slice(0, 3) || []
      }));
      
      console.log('DEBUG: Media data from DDF API:', mediaDebug);
      setDebugData(mediaDebug);

      const saved: string[] = [];
      for (let i = 0; i < Math.min(3, properties.length); i++) {
        const property = properties[i];
        try {
          const listingKey = await saveDDFListingToFirestore(property);
          saved.push(listingKey);
          setStatus(`Saved listing ${i + 1}/3: ${listingKey}`);
        } catch (error: any) {
          console.error(`Error saving listing ${i + 1}:`, error);
          setStatus(`Error saving listing ${i + 1}: ${error.message}`);
        }
      }

      setSavedListings(saved);
      setStatus(`✅ Successfully saved ${saved.length} business listings to Firestore!`);
    } catch (error: any) {
      console.error('Error downloading listings:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>DDF Sync Test | BizBuyMax.com</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">DDF Sync Test</h1>
            <p className="text-gray-600 mb-6">
              Download 3 sample business listings from DDF API and save them to Firestore.
            </p>

            <div className="flex gap-4">
              <button
                onClick={downloadAndSaveSamples}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-md font-medium transition-colors"
              >
                {loading ? 'Downloading...' : 'Download & Save 3 Sample Listings'}
              </button>
              
              <button
                onClick={inspectFirestoreListings}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-md font-medium transition-colors"
              >
                {loading ? 'Inspecting...' : 'Inspect Firestore Data'}
              </button>
            </div>

            {status && (
              <div className={`mt-6 p-4 rounded-md ${
                status.includes('✅') 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : status.includes('❌')
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-blue-50 text-blue-800 border border-blue-200'
              }`}>
                <p className="font-medium">{status}</p>
              </div>
            )}

            {savedListings.length > 0 && (
              <div className="mt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Saved Listings:</h2>
                <ul className="list-disc list-inside space-y-1">
                  {savedListings.map((key, index) => (
                    <li key={key} className="text-gray-700">
                      Listing {index + 1}: {key}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  These listings are now saved in the <code className="bg-gray-100 px-2 py-1 rounded">ddfListings</code> collection.
                  They should appear on the business listings page.
                </p>
              </div>
            )}

            {firestoreData && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Firestore Inspection Results</h2>
                {firestoreData.map((item: any) => (
                  <div key={item.id} className="mb-4 pb-4 border-b border-yellow-200 last:border-0">
                    <h3 className="font-medium text-gray-800 mb-2">{item.title}</h3>
                    <div className="text-sm space-y-1">
                      <p>Document ID: <code className="bg-gray-100 px-1 rounded">{item.id}</code></p>
                      <p>Has media.images: <span className={item.hasMediaImages ? 'text-green-600' : 'text-red-600'}>{item.hasMediaImages ? 'Yes' : 'No'}</span></p>
                      <p>Image count: <strong>{item.imageCount}</strong></p>
                      {item.imageCount > 0 ? (
                        <div>
                          <p className="font-medium mt-2">Sample images (first 3):</p>
                          <div className="space-y-2 mt-2">
                            {item.sampleImages.map((url: string, idx: number) => (
                              <div key={idx} className="bg-white p-2 rounded border border-gray-200">
                                <p className="text-xs break-all mb-2">{url}</p>
                                <img 
                                  src={url} 
                                  alt={`Sample ${idx + 1}`} 
                                  className="w-full h-32 object-cover rounded"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="100" viewBox="0 0 200 100"%3E%3Crect width="200" height="100" fill="%23fee"%3E%3C/rect%3E%3Ctext x="100" y="50" text-anchor="middle" font-size="12" fill="%23c00"%3EImage load failed%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-red-600 mt-2">⚠️ No images stored in Firestore</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {debugData && (
              <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Debug: DDF API Media Data</h2>
                {debugData.map((item: any) => (
                  <div key={item.index} className="mb-4 pb-4 border-b border-gray-200 last:border-0">
                    <h3 className="font-medium text-gray-800 mb-2">Listing {item.index}: {item.listingKey}</h3>
                    <div className="text-sm space-y-1">
                      <p>Has Media Array: <span className={item.hasMedia ? 'text-green-600' : 'text-red-600'}>{item.hasMedia ? 'Yes' : 'No'}</span></p>
                      <p>Media Items: <strong>{item.mediaCount}</strong></p>
                      <p>PhotosCount field: <strong>{item.photoCount}</strong></p>
                      {item.mediaTypes.length > 0 && (
                        <p>Media Types: {item.mediaTypes.join(', ')}</p>
                      )}
                      {item.sampleUrls.length > 0 ? (
                        <div>
                          <p className="font-medium mt-2">Sample Image URLs:</p>
                          <ul className="list-disc list-inside ml-4">
                            {item.sampleUrls.map((url: string, idx: number) => (
                              <li key={idx} className="text-xs break-all">{url}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-red-600 mt-2">⚠️ No image URLs found in Media array</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DDFSyncTest;
